import React from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { chapterFor, journeyMeta, journeyStages } from '../data/journey';
interface JourneyOverlayProps {
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
function Meter({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: string;
}) {
  return <div data-id="element-62" data-mp="2hgsfw5ie4ju3">
      <div className="flex items-baseline justify-between gap-2" data-id="element-63" data-mp="2hh25vt3fqqzs">
        <span className="text-[9px] uppercase tracking-micro text-mut" data-id="element-64" data-mp="3f1wt76dgm7q0">{label}</span>
        <span className="text-[10px] font-semibold tabular-nums text-navy" data-id="element-65" data-mp="3a4ej3cnphmgf">{value}%</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-line" data-id="element-66" data-mp="35blfwt7vym6g">
        <motion.div initial={{
        scaleX: 0
      }} animate={{
        scaleX: value / 100
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} style={{
        transformOrigin: 'left',
        backgroundColor: color
      }} className="h-full" data-id="element-67" data-mp="30ebrngw8ggk7" />
      </div>
    </div>;
}
export function JourneyOverlay({
  onClose
}: JourneyOverlayProps) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-navy/40 px-4 py-6 sm:px-6" role="dialog" aria-modal="true" aria-label={journeyMeta.title} data-id="element-68" data-mp="2g7mqojhwvtol">
      <motion.div initial={{
      opacity: 0,
      y: 12,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="h-fit w-full max-w-4xl overflow-hidden rounded-2xl bg-paper shadow-soft" data-id="element-69" data-mp="1xdpsm3t7j3f0">
        <header className="flex items-start justify-between gap-4 border-b border-line px-6 py-5 sm:px-8" data-id="element-70" data-mp="1sgr01pp6z4ix">
          <div data-id="element-71" data-mp="3hvxg38mry2yw">
            <p className="text-[9px] uppercase tracking-micro text-blue" data-id="element-72" data-mp="3hvfbnopykifw">Internal · campaign arc</p>
            <h2 className="mt-2 font-display text-[19px] font-semibold leading-snug text-navy" data-id="element-73" data-mp="3msxmxlrg79r2">
              {journeyMeta.title}
            </h2>
            <p className="mt-1 text-[12.5px] text-mut" data-id="element-74" data-mp="3gcxiun34usfu">{journeyMeta.subtitle}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close journey" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line text-mut transition-colors duration-150 hover:border-mut hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-75" data-mp="3be46ixzl17h9">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-76" data-mp="30nb356b2mhh" />
          </button>
        </header>

        <div className="px-6 py-7 sm:px-8" data-id="element-77" data-mp="3ub5808txgpgg">
          <div className="flex items-center justify-between rounded-full px-5 py-2" style={{
          background: '#0B2A4A'
        }} data-id="element-78" data-mp="3pewzvfe5dwis">
            <span className="text-[9px] uppercase tracking-micro text-sky" data-id="element-79" data-mp="qq7yc09hs5wk">
              {journeyMeta.axisStart}
            </span>
            <span className="text-[10.5px] font-semibold uppercase tracking-micro text-paper" data-id="element-80" data-mp="u6kk1odlpiat">
              {journeyMeta.axisLabel}
            </span>
            <span className="text-[9px] uppercase tracking-micro text-mint" data-id="element-81" data-mp="y7xp3w8vdhgo">
              {journeyMeta.axisEnd}
            </span>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-3" data-id="element-82" data-mp="186d6v8m0bedi">
            {journeyStages.map((stage, i) => {
            const chapter = chapterFor(stage.snak);
            return <motion.article key={stage.snak} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.26,
              delay: i * 0.07,
              ease: EASE
            }} className="flex flex-col overflow-hidden rounded-2xl border border-line" data-id="element-83" data-mp="1h6a1dqv2v7gh" data-mpi={i}>
                  <div className="px-4 py-3" style={{
                backgroundColor: stage.palette[0]
              }} data-id="element-84" data-mp="1vwzg337qz4p3">
                    <p className="text-[9px] uppercase tracking-micro" style={{
                  color: stage.palette[1]
                }} data-id="element-85" data-mp="2xlcsojtrdfz9">
                      {chapter.label} · {chapter.archetype}
                    </p>
                    <p className="mt-1.5 font-display text-[17px] font-semibold leading-tight text-paper" data-id="element-86" data-mp="37bfwlf2f4ecd">
                      {stage.title}
                    </p>
                    <p className="mt-1 text-[11px] leading-snug text-sky" data-id="element-87" data-mp="32epgwsuizkrh">{stage.subtitle}</p>
                  </div>

                  <div className="flex flex-1 flex-col gap-3 px-4 py-4" data-id="element-88" data-mp="29nxbr5vhfls0">
                    <div className="flex gap-1.5" data-id="element-89" data-mp="29prwaj2udbvk">
                      {stage.palette.map(swatch => <span key={swatch} className="h-5 flex-1 rounded border border-line" style={{
                    backgroundColor: swatch
                  }} aria-hidden="true" data-id="element-90" data-mp="2nlly6i5770z6" />)}
                    </div>

                    <Meter label={journeyMeta.scienceLabel} value={stage.science} color={stage.palette[0]} data-id="element-91" data-mp="6fokwya3q4n7" />
                    <Meter label={journeyMeta.brandLabel} value={stage.brandWeight} color={stage.palette[1]} data-id="element-92" data-mp="3smv8vsp2gnix" />

                    <dl className="mt-1 flex flex-col gap-2.5 border-t border-line pt-3" data-id="element-93" data-mp="p5wofhhpqx3x">
                      {[{
                    label: 'Trust',
                    value: stage.trust
                  }, {
                    label: 'Register',
                    value: stage.register
                  }, {
                    label: 'Design',
                    value: stage.visual
                  }, {
                    label: 'The ask',
                    value: stage.ask
                  }].map(row => <div key={row.label} data-id="element-94" data-mp="184imenhxo83m">
                          <dt className="text-[8.5px] uppercase tracking-micro text-mut" data-id="element-95" data-mp="183ftka11vvl2">
                            {row.label}
                          </dt>
                          <dd className="mt-0.5 text-[11.5px] leading-snug text-navy" data-id="element-96" data-mp="1mqels77qeu0u">
                            {row.value}
                          </dd>
                        </div>)}
                    </dl>

                    <p className="mt-auto pt-2 text-[9px] uppercase tracking-micro text-mut" data-id="element-97" data-mp="1doydq64e33zs">
                      {chapter.differentiation} · {chapter.brandNote}
                    </p>
                  </div>
                </motion.article>;
          })}
          </div>

          <p className="mt-6 border-l-2 border-blue pl-4 text-[13px] leading-relaxed text-mut" data-id="element-98" data-mp="z0bz66ml6g9m">
            {journeyMeta.closing}
          </p>

          <button type="button" onClick={onClose} className="mt-7 flex h-12 w-full items-center justify-center rounded-full border border-line text-[13px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-99" data-mp="q1gpulo8wq23">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>;
}